"use client";

import type React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Heading,
  Text,
  Spinner,
  Flex,
  useToast,
  Grid,
  GridItem,
  Select,
} from "@chakra-ui/react";
import { useAppTheme } from "../ThemeContext.tsx";
import { agentService, type Agent } from "../services/agentService.ts";
import { callService, type Call } from "../services/callService.ts";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Import supabase to get the current user
import { supabase } from "../supabase.ts";

export const DISCONNECTION_COLORS = {
  user_hangup: "#4299E1",
  agent_hangup: "#63B3ED",
  call_transfer: "#ED8936",
  voicemail_reached: "#48BB78",
  inactivity: "#9F7AEA",
  machine_detected: "#F687B3",
  max_duration_reached: "#FC8181",
  concurrency_limit_reached: "#F6AD55",
  no_valid_payment: "#F6E05E",
  scam_detected: "#E53E3E",
  dial_busy: "#C53030",
  dial_failed: "#9B2C2C",
  dial_no_answer: "#742A2A",
  error_inbound_webhook: "#81E6D9",
  error_llm_websocket_open: "#4FD1C5",
  error_llm_websocket_lost_connection: "#38B2AC",
  error_llm_websocket_runtime: "#319795",
  error_llm_websocket_corrupt_payload: "#2C7A7B",
  error_frontend_corrupted_payload: "#285E61",
  error_twilio: "#234E52",
  error_no_audio_received: "#B794F4",
  error_asr: "#805AD5",
  error_retell: "#6B46C1",
  error_unknown: "#553C9A",
  error_user_not_joined: "#44337A",
  registered_call_timeout: "#322659",
} as const;

function DisconnectionReasonChart({
  isDark,
  stackedBarData,
}: {
  isDark: boolean;
  stackedBarData: {
    callSuccess: any[];
    disconnectionReason: any[];
    sentiment: any[];
  };
}) {
  const [showDisconnectionLegend, setShowDisconnectionLegend] = useState(false);

  const pieDataForDisconnection = useMemo(() => {
    const reasons = Object.keys(DISCONNECTION_COLORS);
    return reasons.map((reason) => {
      let total = 0;
      stackedBarData.disconnectionReason.forEach((item) => {
        if (item[reason]) {
          total += item[reason] as number;
        }
      });
      return {
        name: reason
          .replace(/_/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        value: total,
        key: reason,
      };
    });
  }, [stackedBarData.disconnectionReason]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showDisconnectionLegend) {
        const target = event.target as HTMLElement;
        if (!target.closest(".group")) {
          setShowDisconnectionLegend(false);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDisconnectionLegend]);

  return (
    <Box
      p={4}
      borderRadius="lg"
      bg={isDark ? "gray.800" : "white"}
      border="1px solid"
      borderColor={isDark ? "gray.700" : "gray.200"}
      boxShadow="sm"
      height="100%"
    >
      <Flex align="center" mb={2}>
        <Box as="span" mr={2} color={isDark ? "gray.400" : "gray.500"}>
          <img
            loading="lazy"
            width="20"
            height="20"
            decoding="async"
            data-nimg="1"
            src="/pie-chart.e292a4ff.svg"
            alt=""
          />
        </Box>
        <Text
          fontSize="sm"
          fontWeight="medium"
          color={isDark ? "gray.400" : "gray.500"}
        >
          Disconnection Reason
        </Text>
      </Flex>
      <Text fontSize="xs" color={isDark ? "gray.500" : "gray.600"} mb={2}>
        All agents or selected agent
      </Text>
      <Box height="200px" width="100%">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              contentStyle={{
                backgroundColor: isDark ? "#2D3748" : "#fff",
                borderColor: isDark ? "#4A5568" : "#E2E8F0",
              }}
            />
            <Pie
              data={pieDataForDisconnection}
              dataKey="value"
              nameKey="name"
              outerRadius="80%"
            >
              {pieDataForDisconnection.map((entry) => (
                <Cell key={entry.name} fill={DISCONNECTION_COLORS[entry.key]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Flex justify="center" mt={2} flexWrap="wrap" gap={2} my={-2}>
        <Flex align="center">
          <Box
            w="10px"
            h="10px"
            bg={DISCONNECTION_COLORS.agent_hangup}
            mr={1}
          />
          <Text fontSize="xs">Agent Hangup</Text>
        </Flex>
        <Flex align="center">
          <Box
            w="10px"
            h="10px"
            bg={DISCONNECTION_COLORS.call_transfer}
            mr={1}
          />
          <Text fontSize="xs">Call Transfer</Text>
        </Flex>
        <Box
          position="relative"
          className="group"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDisconnectionLegend((prev) => !prev);
          }}
          cursor="pointer"
        >
          <Text fontSize="xs" color="gray.500">
            +{Object.keys(DISCONNECTION_COLORS).length - 2} more
          </Text>
          {showDisconnectionLegend && (
            <Box
              position="absolute"
              bottom="100%"
              left="50%"
              transform="translateX(-50%)"
              bg={isDark ? "gray.800" : "white"}
              border="1px solid"
              borderColor={isDark ? "gray.700" : "gray.200"}
              borderRadius="md"
              p={4}
              minWidth="600"
              zIndex={10}
              boxShadow="lg"
            >
              <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                {Object.entries(DISCONNECTION_COLORS)
                  .filter(
                    ([key]) => !["agent_hangup", "call_transfer"].includes(key)
                  )
                  .map(([key, color], index) => (
                    <Flex key={index} align="center">
                      <Box w="10px" h="10px" bg={color} mr={1} />
                      <Text fontSize="xs">
                        {key
                          .replace(/_/g, " ")
                          .split(" ")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                      </Text>
                    </Flex>
                  ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
}

export default function DashboardHome() {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const [agents, setAgents] = useState<Agent[]>([]);
  const [calls, setCalls] = useState<Call[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string>("all");

  // Add userId state
  const [userId, setUserId] = useState<string | null>(null);

  const toast = useToast();

  const chartgray = "#60A5FA";
  const chartCoral = "#F97316";
  const chartLightgray = "#67E8F9";
  const chartGreen = "#68D391";
  const chartRed = "#CA0B00";

  // Add this useEffect to get the current user ID when the component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Update the fetchData function in the useEffect to pass the userId
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [agentsData, callsData] = await Promise.all([
          agentService.list(),
          callService.listCalls(
            { sort_order: "descending", limit: 1000 },
            userId
          ), // Pass userId here
        ]);
        setAgents(agentsData);
        setCalls(callsData);
      } catch (err) {
        setError("Failed to load dashboard data. Please try again later.");
        toast({
          title: "Error loading dashboard data",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [toast, userId]); // Add userId to the dependency array

  const filteredCalls = useMemo(() => {
    if (selectedAgent === "all") return calls;
    return calls.filter((call) => call.agent_id === selectedAgent);
  }, [calls, selectedAgent]);

  const totalCalls = filteredCalls.length;

  const averageCallDuration = useMemo(() => {
    if (!filteredCalls.length) return 0;
    const totalDuration = filteredCalls.reduce(
      (sum, call) => sum + (call.end_timestamp - call.start_timestamp),
      0
    );
    return totalDuration / filteredCalls.length / 1000;
  }, [filteredCalls]);

  const averageCallLatency = useMemo(() => {
    const validLatencies = filteredCalls
      .filter((call) => call.latency?.e2e?.p50)
      .map((call) => call.latency?.e2e?.p50 ?? 0);
    if (!validLatencies.length) return null;
    const sum = validLatencies.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / validLatencies.length);
  }, [filteredCalls]);

  const callCountData = useMemo(() => {
    const grouped = filteredCalls.reduce((acc, call) => {
      const date = new Date(call.start_timestamp).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [filteredCalls]);

  const callSuccessData = useMemo(() => {
    const successful = filteredCalls.filter(
      (call) => call.call_analysis?.call_successful
    ).length;
    const unsuccessful = totalCalls - successful;
    return [
      { name: "Successful", value: successful, color: chartGreen },
      { name: "Unsuccessful", value: unsuccessful, color: chartRed },
    ];
  }, [filteredCalls, totalCalls, chartGreen, chartRed]);

  const sentimentData = useMemo(() => {
    const sentiments = filteredCalls.reduce((acc, call) => {
      const s = call.call_analysis?.sentiment || "Unknown";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.keys(sentiments).map((name, index) => ({
      name,
      value: sentiments[name],
      color:
        index === 0 ? chartgray : index === 1 ? chartCoral : chartLightgray,
    }));
  }, [filteredCalls, chartgray, chartCoral, chartLightgray]);

  const callDirectionData = useMemo(() => {
    const inbound = filteredCalls.filter(
      (c) => c.call_type === "phone_call" && c.direction === "inbound"
    ).length;

    // Outbound is all web calls plus any phone calls whose direction is "outbound"
    const outbound = filteredCalls.filter(
      (c) =>
        c.call_type === "web_call" ||
        (c.call_type === "phone_call" && c.direction === "outbound")
    ).length;

    return [
      { name: "Inbound", value: inbound, color: chartgray },
      { name: "Outbound", value: outbound, color: chartCoral },
    ];
  }, [filteredCalls, chartgray, chartCoral]);

  const rateData = useMemo(() => {
    if (!filteredCalls.length) {
      return { pickupRate: [], successRate: [], transferRate: [] };
    }
    const sortedCalls = [...filteredCalls].sort(
      (a, b) => a.start_timestamp - b.start_timestamp
    );
    const startDate = new Date(sortedCalls[0].start_timestamp);
    const endDate = new Date(
      sortedCalls[sortedCalls.length - 1].start_timestamp
    );
    const daysDiff = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    let interval: number;
    if (daysDiff <= 7) interval = 1;
    else if (daysDiff <= 30) interval = 7;
    else if (daysDiff <= 365) interval = 30;
    else interval = 90;
    const datePoints: Date[] = [];
    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + interval)
    ) {
      datePoints.push(new Date(d));
    }
    if (!datePoints.length || datePoints[datePoints.length - 1] < endDate) {
      datePoints.push(endDate);
    }
    while (datePoints.length < 4) {
      const ld = new Date(datePoints[datePoints.length - 1]);
      ld.setDate(ld.getDate() + interval);
      datePoints.push(ld);
    }
    function calcRate(points: Date[], fn: (c: Call[]) => number) {
      return points.map((date) => {
        const relevant = sortedCalls.filter(
          (call) =>
            call.start_timestamp <= date.getTime() &&
            call.start_timestamp > date.getTime() - interval * 86400000
        );
        const r = relevant.length ? fn(relevant) : null;
        return {
          date: date.toISOString().split("T")[0],
          rate: r !== null ? r : 0,
        };
      });
    }
    // Put all the "no pickup" disconnection reasons into an array or set:
    const excludedDisconnectionReasons = [
      "voicemail_reached",
      "inactivity",
      "machine_detected",
      "concurrency_limit_reached",
      "no_valid_payment",
      "error_inbound_webhook",
      "dial_busy",
      "dial_failed",
      "dial_no_answer",
      "error_llm_websocket_open",
      "error_llm_websocket_lost_connection",
      "error_llm_websocket_runtime",
      "error_llm_websocket_corrupt_payload",
      "error_frontend_corrupted_payload",
      "error_twilio",
      "error_no_audio_received",
      "error_asr",
      "error_retell",
      "error_unknown",
      "error_user_not_joined",
      "registered_call_timeout",
    ];

    // Then filter calls that ended and are not in the excluded set:
    const pickupRate = calcRate(datePoints, (chunk) => {
      // Calls that ended and don't have a disconnection reason in our excluded list
      const pickedUpCalls = chunk.filter(
        (call) =>
          call.call_status === "ended" &&
          !excludedDisconnectionReasons.includes(call.disconnection_reason)
      );

      // Calculate pickup rate as a percentage
      return (pickedUpCalls.length / chunk.length) * 100;
    });
    const successRate = calcRate(datePoints, (chunk) => {
      const s = chunk.filter((c) => c.call_analysis?.call_successful);
      return (s.length / chunk.length) * 100;
    });
    const transferRate = calcRate(datePoints, (chunk) => {
      const t = chunk.filter((c) => c.disconnection_reason === "call_transfer");
      return (t.length / chunk.length) * 100;
    });
    return { pickupRate, successRate, transferRate };
  }, [filteredCalls]);

  const timeSeriesData = useMemo(() => {
    if (!filteredCalls.length) {
      const d: { date: string; value: number }[] = [];
      return { voicemailRate: d, avgCallDuration: d, avgLatency: d };
    }
    const sorted = [...filteredCalls].sort(
      (a, b) => a.start_timestamp - b.start_timestamp
    );
    const grouped = sorted.reduce((acc, call) => {
      const date = new Date(call.start_timestamp).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
      if (!acc[date]) {
        acc[date] = {
          calls: [] as Call[],
          voicemails: 0,
          totalDuration: 0,
          totalLatency: 0,
        };
      }
      acc[date].calls.push(call);
      if (call.disconnection_reason === "voicemail_reached")
        acc[date].voicemails++;
      acc[date].totalDuration += call.end_timestamp - call.start_timestamp;
      acc[date].totalLatency += call.latency?.e2e?.p50 ?? 0;
      return acc;
    }, {} as Record<string, { calls: Call[]; voicemails: number; totalDuration: number; totalLatency: number }>);
    const metrics = Object.entries(grouped).map(([date, data]) => {
      const count = data.calls.length;
      return {
        date,
        voicemailRate: (data.voicemails / count) * 100,
        avgCallDuration: data.totalDuration / count / 1000,
        avgLatency: data.totalLatency / count,
      };
    });
    return {
      voicemailRate: metrics.map((m) => ({
        date: m.date,
        value: m.voicemailRate,
      })),
      avgCallDuration: metrics.map((m) => ({
        date: m.date,
        value: m.avgCallDuration,
      })),
      avgLatency: metrics.map((m) => ({ date: m.date, value: m.avgLatency })),
    };
  }, [filteredCalls]);

  const stackedBarData = useMemo(() => {
    if (!filteredCalls.length) {
      return { callSuccess: [], disconnectionReason: [], sentiment: [] };
    }
    const grouped = filteredCalls.reduce(
      (acc, call) => {
        const date = new Date(call.start_timestamp).toLocaleDateString(
          "en-US",
          {
            year: "numeric",
            month: "short",
            day: "2-digit",
          }
        );
        if (!acc[date]) {
          acc[date] = {
            successful: 0,
            unsuccessful: 0,
            unknown: 0,
            reasons: {} as Record<string, number>,
            negative: 0,
            neutral: 0,
            positive: 0,
            sentiment_unknown: 0,
          };
        }
        const success = call.call_analysis?.call_successful;
        if (success === true) acc[date].successful++;
        else if (success === false) acc[date].unsuccessful++;
        else acc[date].unknown++;
        const reason = call.disconnection_reason;
        if (!acc[date].reasons[reason]) acc[date].reasons[reason] = 0;
        acc[date].reasons[reason]++;
        const sent = call.call_analysis?.sentiment || "unknown";
        if (sent === "negative") acc[date].negative++;
        else if (sent === "neutral") acc[date].neutral++;
        else if (sent === "positive") acc[date].positive++;
        else acc[date].sentiment_unknown++;
        return acc;
      },
      {} as Record<
        string,
        {
          successful: number;
          unsuccessful: number;
          unknown: number;
          reasons: Record<string, number>;
          negative: number;
          neutral: number;
          positive: number;
          sentiment_unknown: number;
        }
      >
    );
    const callSuccess = Object.entries(grouped).map(([date, data]) => ({
      date,
      successful: data.successful,
      unknown: data.unknown,
      unsuccessful: data.unsuccessful,
    }));
    const disconnectionReason = Object.entries(grouped).map(([date, data]) => {
      const row: Record<string, number | string> = { date };
      Object.keys(DISCONNECTION_COLORS).forEach((r) => {
        row[r] = data.reasons[r] || 0;
      });
      return row;
    });
    const sentiment = Object.entries(grouped).map(([date, data]) => ({
      date,
      negative: data.negative,
      neutral: data.neutral,
      positive: data.positive,
      unknown: data.sentiment_unknown,
    }));
    return { callSuccess, disconnectionReason, sentiment };
  }, [filteredCalls]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <Text color="red.500">{error}</Text>
      </Flex>
    );
  }

  const MetricCard = ({
    title,
    value,
    subtitle,
  }: {
    title: string;
    value: string;
    subtitle: string;
  }) => (
    <Box
      p={4}
      borderRadius="lg"
      bg={isDark ? "gray.800" : "white"}
      border="1px solid"
      borderColor={isDark ? "gray.700" : "gray.200"}
      boxShadow="sm"
      height="100%"
    >
      <Flex direction="column" height="100%">
        <Flex align="center" mb={2}>
          <Box as="span" mr={2} color={isDark ? "gray.400" : "gray.500"}>
            <img
              loading="lazy"
              width="20"
              height="20"
              decoding="async"
              data-nimg="1"
              src="/big-number.18eff737.svg"
              alt=""
            />
          </Box>
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={isDark ? "gray.400" : "gray.500"}
          >
            {title}
          </Text>
        </Flex>
        <Text fontSize="xs" color={isDark ? "gray.500" : "gray.600"} mb={2}>
          {subtitle}
        </Text>
        <Text fontSize="2xl" fontWeight="bold" mt="auto">
          {value}
        </Text>
      </Flex>
    </Box>
  );

  const ChartCard = ({
    title,
    subtitle,
    children,
    icon,
  }: {
    title: string;
    subtitle: string;
    children: React.ReactNode;
    icon?: React.ReactNode;
  }) => (
    <Box
      p={4}
      borderRadius="lg"
      bg={isDark ? "gray.800" : "white"}
      border="1px solid"
      borderColor={isDark ? "gray.700" : "gray.200"}
      boxShadow="sm"
      height="100%"
    >
      <Flex align="center" mb={2}>
        <Box as="span" mr={2} color={isDark ? "gray.400" : "gray.500"}>
          {icon || (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L2 12L12 22L22 12L12 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Text
          fontSize="sm"
          fontWeight="medium"
          color={isDark ? "gray.400" : "gray.500"}
        >
          {title}
        </Text>
      </Flex>
      <Text fontSize="xs" color={isDark ? "gray.500" : "gray.600"} mb={2}>
        {subtitle}
      </Text>
      <Box height="200px" width="100%">
        {children}
      </Box>
    </Box>
  );

  return (
    <Box p={4} bg={isDark ? "gray.900" : "gray.50"}>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap">
        <Heading size="lg" mb={{ base: 2, md: 0 }}>
          Dashboard Overview
        </Heading>
        <Select
          value={selectedAgent}
          onChange={(e) => setSelectedAgent(e.target.value)}
          width="200px"
          mb={{ base: 2, md: 0 }}
          bgColor={isDark ? "gray.700" : "white"}
          color={isDark ? "gray.300" : "gray.700"}
          borderColor={isDark ? "gray.600" : "gray.200"}
        >
          <option
            value="all"
            style={{
              backgroundColor: isDark ? "gray.700" : "white",
              color: isDark ? "gray.300" : "gray.700",
            }}
          >
            All Agents
          </option>
          {agents.map((agent) => (
            <option
              key={agent.agent_id}
              value={agent.agent_id}
              style={{
                backgroundColor: isDark ? "gray.700" : "white",
                color: isDark ? "gray.300" : "gray.700",
              }}
            >
              {agent.agent_name || agent.agent_id}
            </option>
          ))}
        </Select>
      </Flex>

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={4}
        mb={4}
      >
        <GridItem>
          <MetricCard
            title="Call Counts"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            value={totalCalls.toLocaleString()}
          />
        </GridItem>
        <GridItem>
          <MetricCard
            title="Avg Call Duration"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            value={`${Math.floor(averageCallDuration)}s`}
          />
        </GridItem>
        <GridItem>
          <MetricCard
            title="Avg Call Latency"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            value={averageCallLatency ? `${averageCallLatency}ms` : "N/A"}
          />
        </GridItem>
      </Grid>

      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4} mb={4}>
        <GridItem>
          <ChartCard
            title="Call Counts"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/line-chart.7a36f55d.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={callCountData}
                margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id="callCountGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#60A5FA"
                  fill="url(#callCountGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </GridItem>
        <GridItem>
          <ChartCard
            title="Call Successful"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/pie-chart.e292a4ff.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={callSuccessData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {callSuccessData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                  formatter={(value: number, name: string) => [
                    `${value} (${((value / totalCalls) * 100).toFixed(2)}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <Flex justify="center" mt={2}>
              {callSuccessData.map((entry, idx) => (
                <Flex key={idx} align="center" mx={2} my={-10}>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={entry.color}
                    mr={1}
                  />
                  <Text fontSize="xs">{entry.name}</Text>
                </Flex>
              ))}
            </Flex>
          </ChartCard>
        </GridItem>
      </Grid>

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={4}
        mb={4}
      >
        <GridItem>
          <DisconnectionReasonChart
            isDark={isDark}
            stackedBarData={stackedBarData}
          />
        </GridItem>
        <GridItem>
          <ChartCard
            title="User Sentiment"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/pie-chart.e292a4ff.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sentimentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={0}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {sentimentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <Flex justify="center" mt={2}>
              {sentimentData.map((entry, index) => (
                <Flex key={index} align="center" mx={2} my={-10}>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={entry.color}
                    mr={1}
                  />
                  <Text fontSize="xs">{entry.name}</Text>
                </Flex>
              ))}
            </Flex>
          </ChartCard>
        </GridItem>
        <GridItem>
          <ChartCard
            title="Phone inbound/outbound"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/pie-chart.e292a4ff.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={callDirectionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={0}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {callDirectionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <Flex justify="center" mt={2}>
              {callDirectionData.map((entry, index) => (
                <Flex key={index} align="center" mx={2} my={-10}>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={entry.color}
                    mr={1}
                  />
                  <Text fontSize="xs">{entry.name}</Text>
                </Flex>
              ))}
            </Flex>
          </ChartCard>
        </GridItem>
      </Grid>

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={4}
        mb={4}
      >
        <GridItem>
          <ChartCard
            title="Call Picked Up Rate"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/line-chart.7a36f55d.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={rateData.pickupRate.length ? rateData.pickupRate : []}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id="pickupGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}%`,
                    "Rate",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke="#60A5FA"
                  fill="url(#pickupGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </GridItem>

        <GridItem>
          <ChartCard
            title="Call Successful Rate"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/line-chart.7a36f55d.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={rateData.successRate.length ? rateData.successRate : []}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id="successGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}%`,
                    "Rate",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke="#60A5FA"
                  fill="url(#successGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </GridItem>

        <GridItem>
          <ChartCard
            title="Call Transfer Rate"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/line-chart.7a36f55d.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={rateData.transferRate.length ? rateData.transferRate : []}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id="transferGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}%`,
                    "Rate",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke="#60A5FA"
                  fill="url(#transferGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </GridItem>
      </Grid>

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={4}
        mb={4}
      >
        <GridItem>
          <ChartCard
            title="Voicemail Rate"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/line-chart.7a36f55d.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={timeSeriesData.voicemailRate}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}%`,
                    "Voicemail rate",
                  ]}
                />
                <defs>
                  <linearGradient
                    id="voicemailGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#60A5FA"
                  fill="url(#voicemailGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </GridItem>
        <GridItem>
          <ChartCard
            title="Average Call Duration"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/line-chart.7a36f55d.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={timeSeriesData.avgCallDuration}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}s`,
                    "Duration",
                  ]}
                />
                <defs>
                  <linearGradient
                    id="durationGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#60A5FA"
                  fill="url(#durationGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </GridItem>
        <GridItem>
          <ChartCard
            title="Average Latency"
            subtitle={selectedAgent === "all" ? "All agents" : "Selected agent"}
            icon={
              <img
                loading="lazy"
                width="20"
                height="20"
                decoding="async"
                data-nimg="1"
                src="/line-chart.7a36f55d.svg"
                alt=""
              />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={timeSeriesData.avgLatency}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: isDark ? "#A0AEC0" : "#4A5568" }}
                  stroke={isDark ? "#4A5568" : "#E2E8F0"}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? "#2D3748" : "#fff",
                    borderColor: isDark ? "#4A5568" : "#E2E8F0",
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}ms`,
                    "Latency",
                  ]}
                />
                <defs>
                  <linearGradient
                    id="latencyGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#60A5FA"
                  fill="url(#latencyGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </GridItem>
      </Grid>
    </Box>
  );
}
